import type { Parcel } from "../../viewer/types";
import { shortId } from "./adminShared";

/**
 * RIGHT pane shown when the admin clicks a parcel in the client grid that does
 * NOT belong to any Territory / publication — there is nothing publicly
 * moderatable, so we surface the raw parcel facts instead of the Inspector.
 */
export function ParcelModerationNote({ parcel }: { parcel: Parcel }) {
  return (
    <div data-testid="review-parcel-note" data-parcel-id={parcel.id} className="panel-chamfer panel-chamfer--sm">
      <div className="chamfer-strip">
        <span className="strip-dot" aria-hidden />
        <span>Parcel #{parcel.id}</span>
      </div>
      <div className="p-4 space-y-4">
        <div className="rounded-md border border-border-soft bg-obsidian/40 p-6 text-center">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">Moderation</div>
          <div className="mt-2 font-display text-[15px] text-text-secondary">No Published Content to Moderate</div>
          <p className="mt-2 text-[11.5px] text-text-muted leading-relaxed">
            This parcel is not part of any published Territory yet, so there is nothing public to
            hide or restore. It becomes moderatable the moment its owner publishes a Territory that
            includes it.
          </p>
        </div>

        <dl className="space-y-1.5">
          <Row label="Parcel">
            <span className="font-mono text-[11px] text-text-primary tabular-nums">#{parcel.id}</span>
          </Row>
          <Row label="Owner">
            <span className="font-mono text-[11px] text-text-secondary break-all">{shortId(parcel.owner, 10, 8)}</span>
          </Row>
          <Row label="Position">
            <span className="font-mono text-[11px] text-text-secondary tabular-nums">({parcel.x}, {parcel.y})</span>
          </Row>
          <Row label="Size">
            <span className="font-mono text-[11px] text-text-secondary tabular-nums">{parcel.w}×{parcel.h}</span>
          </Row>
        </dl>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="w-24 shrink-0 font-mono text-[8.5px] uppercase tracking-[0.18em] text-text-muted pt-0.5">{label}</dt>
      <dd className="m-0 flex-1 text-[12px]">{children}</dd>
    </div>
  );
}
